import * as React from 'react';
import type { GetStaticProps } from 'next';
import { queryPageBySlug, queryFooter, queryNavbar, querySectionsBySystemIds } from '@/clients/queries';
import { FooterQuery, NavbarQuery, PageBySlugQuery, PageSectionsBySystemIdQuery } from 'types/generated';
import { chunk } from '@/utils/arrays';
import { getOr } from '@/utils/options';
import HafaSambandPage from '@/contentful/HafaSambandPage';
import config from '@/config/config';
import PageContainer from '@/components/PageContainer';
import { apolloClient, getClient } from '@/utils/contentful';
import { RadioSchedule } from '@/models/RadioTypes';
import { getRadioSchedules } from '@/clients/RadioUtils';
import { Log } from '@/logging/datadog';

type Props = {
  page: PageBySlugQuery;
  navbar: NavbarQuery;
  footer: FooterQuery;
  sections: Array<PageSectionsBySystemIdQuery>;
  radioSchedules: Record<string, RadioSchedule[]>;
  orderedIds: Array<string>;
};

const RouteComponent = ({ navbar, footer, page, sections, radioSchedules, orderedIds }: Props) => {
  const pageCollection = page.pageCollection?.items[0];

  if (!pageCollection) {
    throw new Error('no page collection found');
  }

  return (
    <PageContainer
      title={getOr(pageCollection.title, 'Sýn')}
      metaTags={pageCollection.metaTags}
      contactFormSetting={pageCollection.contactFormSetting}
      navbar={navbar}
      footer={footer}
      radioSchedules={radioSchedules}
    >
      <HafaSambandPage sections={sections} orderedIds={orderedIds} />
    </PageContainer>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const slug = 'hafa-samband';
  const inPreview = getOr(context.draftMode, false);
  try {
    const client = getClient(inPreview);

    const contentfulPage = (await queryPageBySlug(client, slug, inPreview)).data;

    if (!contentfulPage.pageCollection) {
      throw new Error(`no page collection found in graphql response when fetching page details for ${slug}`);
    }

    if (!contentfulPage.pageCollection.items) {
      throw new Error(`no items array set in page details for ${slug}`);
    }

    const page = contentfulPage.pageCollection.items[0] || undefined;

    if (!page) {
      throw new Error(`no page component found in graphql response for slug ${slug}`);
    }

    const sectionIds = (page.sectionsCollection?.items.map((x) => x?.sys.id) || []) as string[];

    const chunkedSections = chunk(sectionIds, config.SECTION_CHUNK_SIZE).map((c) => {
      return querySectionsBySystemIds(client, c, inPreview).then((x) => x.data);
    });

    return {
      props: {
        navbar: (await queryNavbar(apolloClient)).data,
        footer: (await queryFooter(apolloClient)).data,
        page: contentfulPage,
        sections: await Promise.all(chunkedSections),
        radioSchedules: await getRadioSchedules(),
        orderedIds: sectionIds,
      },
    };
  } catch (err) {
    Log.error((err as Error).message || '', err);
    throw err;
  }
};

export default RouteComponent;
